import {
    useCallback, useEffect, useReducer, useRef, useState
} from 'react';
import logging from '@wr/logging';
import {genericErrorHasOccurred} from '../../../actions/generic-error/action-creators';
import {
    setAllTouched,
    setCardType,
    setErrors,
    setIsSubmitting,
    setTouched,
    setValue
} from '../../../actions/zepzPayin/action-creators';
import {getInitData, initTransaction} from '../../../actions/zepzPayin/browser';
import eventIds from '../../../event-ids';
import config from '../../../config';
import {zepzFormReducer} from '../../../reducers/zepz-form';
import {ERROR_TYPE, FORM_CONFIG, NOT_SUPPORTED_CARD_TYPES} from './consts';
import {FIELDS} from './zepz-form.fields';
import {TokenExIframe} from './zepz-form.services';

const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);

export const useZepzInitData = ({
    initData = null, languageCode, paymentSessionId, dispatch
}) => {
    const [data, setData] = useState(initData);
    const [error, setError] = useState(null);

    const catchError = err => {
        dispatch(genericErrorHasOccurred(true));

        setError(err);
    };

    useEffect(() => {
        if (!data && !error) {
            getInitData({languageCode, paymentSessionId})
                .then(setData)
                .catch(catchError);
        }
    }, [initData]);

    return {
        data,
        error
    };
};

export const useZepzIframe = ({
    container,
    cvvContainer,
    translations,
    initData
}) => {
    const [iframe, setIframe] = useState(null);

    useEffect(() => {
        const {origin} = window.location;

        const containerID = container.current?.id;
        const cvvContainerID = cvvContainer.current?.id;

        if (!initData || iframe || !containerID || !cvvContainerID) {
            return undefined;
        }

        const iframeConfig = {
            ...FORM_CONFIG,
            origin,
            timestamp: initData.timestamp,
            authenticationKey: initData.authenticationKey,
            tokenExID: initData.tokenexId,
            tokenScheme: initData.scheme,
            cvvContainerID,
            placeholder: translations.zepz.cardNumberPlaceholder,
            cvvPlaceholder: translations.zepz.labels.securityCode
        };

        const newIframe = new TokenExIframe(containerID, iframeConfig);
        newIframe.load();
        newIframe.onLoad(() => {
            setIframe(newIframe);
        });

        newIframe.onError(err => {
            logger.error('Received TokenEx iframe error', err.message);
        });

        return () => {
            newIframe.remove();
        };
    }, [initData]);

    return {
        iframe
    };
};
const INIT_STATE = {
    errors: {
        [FIELDS.cardNumber.name]: '',
        [FIELDS.expiryDate.name]: '',
        [FIELDS.securityCode.name]: ''
    },
    touched: {
        [FIELDS.cardNumber.name]: false,
        [FIELDS.expiryDate.name]: false,
        [FIELDS.securityCode.name]: false
    },
    values: {
        [FIELDS.expiryDate.name]: FIELDS.expiryDate.defaultValue,
        [FIELDS.shouldSaveCard.name]: FIELDS.shouldSaveCard.defaultValue
    },
    isValid: false,
    isSubmitting: false,
    areAllTouched: false,
    possibleCardType: ''
};

export const useZepzValidation = ({state, dispatch, iframe}) => {
    const validate = ({
        isValid: isCardNumberValid, isCvvValid, validator, cvvValidator
    }) => {
        const {touched, possibleCardType} = state;
        const newErrors = {...INIT_STATE.errors};
        if (!isCardNumberValid && touched[FIELDS.cardNumber.name]) {
            newErrors[FIELDS.cardNumber.name] = validator;
        }

        if (!isCvvValid && touched[FIELDS.securityCode.name]) {
            newErrors[FIELDS.securityCode.name] = cvvValidator;
        }

        const dateError = FIELDS.expiryDate.validator(state.values[FIELDS.expiryDate.name]);

        if (dateError && touched[FIELDS.expiryDate.name]) {
            newErrors[FIELDS.expiryDate.name] = dateError;
        }

        if (possibleCardType && NOT_SUPPORTED_CARD_TYPES.includes(possibleCardType)) {
            newErrors[FIELDS.cardNumber.name] = ERROR_TYPE.notSupportedType;
        }

        dispatch(setErrors(newErrors));
    };


    const validateRef = useRef(validate);
    validateRef.current = validate;


    useEffect(() => {
        iframe?.onCardTypeChange(({possibleCardType}) => dispatch(setCardType(possibleCardType)));
        iframe?.onValidate(data => validateRef?.current(data));
    }, [iframe]);

    useEffect(() => {
        iframe?.validate();
    }, [state.values, state.touched, state.possibleCardType]);
};

export const useZepzForm = ({
    iframe, validation = useZepzValidation, csrfToken, platform, threeDSData, dispatch
}) => {
    const [state, _dispatch] = useReducer(zepzFormReducer, INIT_STATE);
    validation?.({iframe, dispatch: _dispatch, state});

    const setElementTouched = name => _dispatch(setTouched(name));
    const setElementValue = (name, value) => _dispatch(setValue(name, value));

    const onChange = useCallback((name, value) => {
        setElementValue(name, value);
    }, []);


    const onBlur = useCallback(name => {
        setElementTouched(name);
    }, []);

    const handleTokenize = data => {
        const {
            firstSix,
            lastFour,
            token,
            tokenHMAC,
            cardType
        } = data;

        const [year, month] = state.values[FIELDS.expiryDate.name].split('/');

        return initTransaction({
            threeDSData,
            platform,
            csrfToken,
            isNewCard: true,
            body: {
                bin: firstSix,
                expiry: {
                    month,
                    year
                },
                panSuffix: lastFour,
                schema: cardType.toUpperCase(),
                tokenexToken: token,
                tokenexHash: tokenHMAC,
                save: state.values[FIELDS.shouldSaveCard.name]
            }
        }).catch(error => {
            dispatch(genericErrorHasOccurred(true));
            logger.error(`Error retrieving Zepz Payin init data: ${error.message}`, {
                error: error.message,
                eventId: eventIds['Error retrieving Zepz Payin init data']
            });
        });
    };


    const handleTokenizeRef = useRef(handleTokenize);
    handleTokenizeRef.current = handleTokenize;

    useEffect(() => {
            iframe?.onTokenize((...args) => handleTokenizeRef?.current(...args));
            iframe?.onBlur(() => onBlur(FIELDS.cardNumber.name, {}));
            iframe?.onCvvBlur(() => onBlur(FIELDS.securityCode.name, {}));
    }, [iframe]);


    const onSubmit = event => {
        event.preventDefault();

        if (!state.areAllTouched) {
            _dispatch(setAllTouched());
        } else if (state.isValid) {
            _dispatch(setIsSubmitting(true));
            iframe.tokenize();
        }
    };

    return {
        onChange,
        onBlur,
        onSubmit,
        state,
        dispatch: _dispatch
    };
};
